import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Lightbulb, TrendingDown, Layers } from 'lucide-react';
import { useObsOptimization } from '@/hooks/useObservability';
import { LoaderBlock, fmtUSDCents } from './shared';

const FINDING_META: Record<string, { labelKey: string; icon: any; tone: string; descKey: string }> = {
  multi_llm_subs: {
    labelKey: 'observability.optimization.finding_multi_llm',
    icon: Layers, tone: 'amber',
    descKey: 'observability.optimization.finding_multi_llm_desc',
  },
  sub_without_recent_use: {
    labelKey: 'observability.optimization.finding_sub_no_use',
    icon: TrendingDown, tone: 'rose',
    descKey: 'observability.optimization.finding_sub_no_use_desc',
  },
  ai_native_only_no_aitool: {
    labelKey: 'observability.optimization.finding_ai_native',
    icon: Lightbulb, tone: 'blue',
    descKey: 'observability.optimization.finding_ai_native_desc',
  },
};

export function ObsOptimizationTab() {
  const { t } = useTranslation();
  const { data: findings = [], isLoading } = useObsOptimization();

  const grouped = useMemo(() => {
    const map = new Map<string, typeof findings>();
    for (const f of findings) {
      const arr = map.get(f.finding_type) || [];
      arr.push(f);
      map.set(f.finding_type, arr);
    }
    return Array.from(map.entries());
  }, [findings]);

  if (isLoading) return <LoaderBlock />;

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-amber-50 dark:bg-amber-900/10 border-amber-200">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-sm">{t('observability.optimization.advisor_title')}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('observability.optimization.advisor_desc')}
            </p>
          </div>
        </div>
      </Card>

      {grouped.map(([type, items]) => {
        const meta = FINDING_META[type] || { labelKey: type, icon: AlertCircle, tone: 'blue', descKey: '' };
        const Icon = meta.icon;
        const toneClasses: Record<string, string> = {
          blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30',
          amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30',
          rose: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30',
        };
        return (
          <Card key={type} className="p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-start gap-3">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${toneClasses[meta.tone]}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{t(meta.labelKey, meta.labelKey)}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{meta.descKey ? t(meta.descKey) : ''}</p>
                </div>
              </div>
              <Badge variant="secondary">
                {items.length} {items.length === 1 ? t('observability.optimization.case') : t('observability.optimization.cases')}
              </Badge>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-3">{t('observability.optimization.col_user')}</th>
                    <th>{t('observability.optimization.col_detail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, 30).map((f, i) => {
                    let parsed: any = {};
                    try { parsed = JSON.parse(f.details); } catch { /* details no es JSON → se muestra vacío */ }
                    return (
                      <tr key={i} className="border-b hover:bg-muted/50">
                        <td className="py-1.5 pr-3 text-xs">{f.user_email || '—'}</td>
                        <td className="text-xs">
                          {parsed.vendors && (
                            <span className="mr-3">
                              <strong>Vendors:</strong> {parsed.vendors}
                            </span>
                          )}
                          {parsed.ai_native_vendors && (
                            <span className="mr-3">
                              <strong>AI Native:</strong> {parsed.ai_native_vendors}
                            </span>
                          )}
                          {parsed.total_usd && (
                            <span className="mr-3"><strong>USD:</strong> {fmtUSDCents(parsed.total_usd)}</span>
                          )}
                          {parsed.usd && (
                            <span className="mr-3"><strong>USD:</strong> {fmtUSDCents(parsed.usd)}</span>
                          )}
                          {parsed.n_vendors && (
                            <span className="mr-3 text-muted-foreground">({parsed.n_vendors} vendors)</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {items.length > 30 && (
                <div className="text-xs text-muted-foreground mt-3">
                  {t('observability.optimization.showing_items', { total: items.length })}
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
